import React from 'react'
import { Box, Flex, Link, Table, Text, useAppTheme } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'
import { useRulesQuery } from 'queries/wage/useRulesQuery'
import { useRulesContextValue } from '../context/RulesProvider'
import styles from './RulesTable.module.css'
import * as ROUTES from 'constants/routes'

type Rule = NonNullable<ReturnType<typeof useRulesQuery>['data']>[number]

const SCOPE_LABELS: Record<string, string> = {
  full: 'Full',
  unincorporated_only: 'Unincorporated only',
}

const columns: ColumnDef<Rule>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    size: 80,
  },
  {
    header: 'Effective Start',
    accessorKey: 'effectiveStart',
    size: 140,
  },
  {
    header: 'Effective End',
    accessorKey: 'effectiveEnd',
    size: 140,
    cell: ({ row }) =>
      row.original.effectiveEnd ?? <Text color="lightText">Open-ended</Text>,
  },
  {
    header: 'Coverage Scope',
    accessorKey: 'coverageScope',
    size: 180,
    cell: ({ row }) =>
      SCOPE_LABELS[row.original.coverageScope] ?? row.original.coverageScope,
  },
  {
    header: 'Reference',
    accessorKey: 'reference',
    cell: ({ row }) =>
      row.original.reference ? (
        <span className={styles.referenceCell} title={row.original.reference}>
          {row.original.reference}
        </span>
      ) : (
        <Text color="lightText">—</Text>
      ),
  },
  {
    header: 'Jurisdictions',
    id: 'jurisdictions',
    cell: ({ row }) =>
      (row.original.jurisdictions ?? [])
        .map((j) => (j.stateCode ? `${j.name}, ${j.stateCode}` : j.name))
        .join(' · '),
  },
  {
    header: 'Detail',
    id: 'detail',
    size: 80,
    cell: ({ row }) => {
      const { colors } = useAppTheme()
      return (
        <Link
          to={ROUTES.WAGE_RULE_DETAIL.replace(
            ':ruleId',
            String(row.original.id)
          )}
        >
          View <ArrowRight size={14} color={colors.primary} />
        </Link>
      )
    },
  },
]

export const RulesTable = () => {
  const {
    state: { filteredRules, pagination },
    actions: { setPagination },
  } = useRulesContextValue()

  if (filteredRules.length === 0) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height={258}
        color="lightText"
      >
        <Text>No rules matched your search.</Text>
      </Flex>
    )
  }

  return (
    <Box>
      <Table
        columns={columns}
        data={filteredRules}
        enableSorting
        clientPagination={{ state: pagination, setState: setPagination }}
        textVerticalAlign="middle"
      />
    </Box>
  )
}
